import { useQuery } from '@apollo/client/react';
import { getImageUrl } from '@website/components/media/utils/imgix';
import ErrorPage from '@website/components/utilities/ErrorPage';
import {
  CheckoutUserCreditsDocument,
  GroupForBookingFormDocument,
  GroupForBookingFormQuery,
} from '@website/types/graphqlOperations';
import { Ecommerce, useEventFan } from 'eventfan';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { buildURL } from 'react-imgix';
import { Store } from '../../Store';
import SeoMetaTags from '../seoMetaTags';
import LoadingPage from '../utilities/LoadingPage';
import NewBookingForm from './BookingForm';
import CheckoutLogin from './CheckoutLogin';

interface BookPageInnerProps {
  group: GroupForBookingFormQuery['group'];
}

/**
 * Book page content
 */
export function BookPageInner({ group }: BookPageInnerProps) {
  // Get the state for user ID
  const { state } = useContext(Store);
  const { track } = useEventFan();

  const trip = group?.trip;

  const { loading, data } = useQuery(CheckoutUserCreditsDocument, {
    variables: { id: state.user.id, currency: group.guestPrice.currency },
    // Get the latest availability using the network
    fetchPolicy: 'network-only',
  });

  const credit = data?.user?.totalCredits.amountZD;

  useEffect(() => {
    track<Ecommerce.CheckoutStepCompleted>('Checkout Step Completed', {
      step: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle loading (including trip not available yet, as trigger races with loading state)
  if (loading) {
    return <LoadingPage />;
  }

  return (
    <>
      <SeoMetaTags
        title={`Trip Reservation - ${trip.title}`}
        description={trip.description}
        imageKey={trip.image.file?.key}
        showWebsiteName
      />
      <Container className="mt-5 mb-5">
        <NewBookingForm customerCreditBalance={credit} group={group} />
      </Container>
    </>
  );
}

/**
 * Book a trip page content
 *
 * Send checkout tracking event for Checkout Started and Viewed a Product before
 * Authentication.
 *
 * Requires authentication and is Client Side Renderred
 */
export default function BookPageContent() {
  const { query } = useRouter();
  const { track } = useEventFan();
  const { groupID } = query;

  const [isTrackCheckoutStartedEventSent, setisTrackCheckoutStartedEventSent] =
    useState(false);

  // Get global state (for user)
  const { state } = useContext(Store);

  const { data, loading, error } = useQuery(GroupForBookingFormDocument, {
    variables: { id: groupID as string },
    // Get the latest availability using the network (but use the cache to render first)
    fetchPolicy: 'cache-and-network',
    // skip query if the groupID is not undefined)
    skip: groupID === undefined,
  });

  const group = data?.group;

  // visitor views a specific trip
  useEffect(() => {
    if (group?.trip && !isTrackCheckoutStartedEventSent) {
      const { trip } = group;

      track<Ecommerce.CheckoutStarted>('Checkout Started', {
        order_id: undefined as any, // We don't have this yet
        revenue: group.guestPrice.amountZD / 100,
        currency: group.guestPrice.currency,
        products: [
          {
            product_id: trip.id,
            sku: group.id,
            name: trip.title,
            brand: trip.host.name,
            variant: group.id,
            price: group.guestPrice.amountZD / 100,
            url: `${process.env.NEXT_PUBLIC_DOMAIN}/trips/${trip.urlSlug}`,
            image_url: buildURL(`${getImageUrl(trip.image.file?.key)}`),
          },
        ],
      });

      setisTrackCheckoutStartedEventSent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  // show loading if groupID is not defined, or is authenticating
  if (loading || !groupID || state.isAuthenticating) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage />;
  }

  // Show login page if not authenticated
  if (!state.isAuthenticated) {
    return <CheckoutLogin group={group} />;
  }

  return <BookPageInner group={group} />;
}

export interface BookingSetSuccess {
  values: {
    quantity: number;
  };
}
